import { faHeart, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import {
    faBan,
    faCog,
    faEraser,
    faExclamationTriangle,
    faPencil,
    faTh,
    faTrash,
    faUndo,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIconProps } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { Location } from "history";
import React from "react";

import {
    MAGAZINE_USER,
    MK_TESTING_USERNAME,
    MODRYKONIK_USERNAME,
} from "@/mk2/apps/blogs/constants";
import { BLOG_ALBUM_MENU_MORE } from "@/mk2/apps/blogs/helpers";
import {
    blogsAlbumEditUrl,
    blogsAlbumReorderUrl,
    blogsEntityLikersUrl,
} from "@/mk2/apps/blogs/urls";
import { heartsUrl } from "@/mk2/apps/hearts/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { MenuItem } from "@/mk2/components/menu/MenuItem";
import { ComplaintTargetType } from "@/mk2/containers/Complaint/Complaint.schemas";
import { Menu } from "@/mk2/containers/Menu/Menu";
import { url } from "@/mk2/helpers/urls";
import { BlogPostEntity, UserEntity } from "@/mk2/schemas";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { interpolate } from "@/mk2/services/i18n";
import {
    ADD_TO_MAGAZINE,
    BLOCK_USER,
    BLOG_REPORT_POST,
    CHANGE_DATE_TITLE_AND_DESCRIPTION,
    DELETE_ALBUM,
    REMOVE_FROM_FEED,
    REMOVE_FROM_MAGAZINE,
    REORDER_PHOTOS,
    RETURN_TO_FEED,
    SEND_HEART_TO_username,
    SHOW_USERS_WHO_LIKED_THIS_POST,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

import styles from "./AlbumMenu.mscss";

const fasCogLg: FontAwesomeIconProps = { icon: faCog, size: "lg" };

interface OwnProps {
    className?: string;
    isAdmin: boolean;
    isAuthor: boolean;
    post: BlogPostEntity;
    requestUser: UserEntity;
    location: Location;

    onDelete(postId: number);
    onIgnoreUser(username: string);
    onBlockUser?(userId: number, username: string);
    onReport(entityType: ComplaintTargetType, postId: number, location: HistoryLocationWithState);
    onSetDisplayingAtFeed(postId: number, displaying: boolean);
    onSetShowInMagazine?(postId: number, showInMagazine: boolean);
    onToggleMenuMore(postId: number);
}

type Props = OwnProps;

class AlbumMenu extends React.PureComponent<Props> {
    public render() {
        const { className, isAdmin, isAuthor, location, post, requestUser } = this.props;

        const showReport = !isAuthor && isAuthenticated(requestUser) && !post.deletedAt;
        const showSendHeart = !isAuthor && isAuthenticated(requestUser);
        const showBlock = !isAuthor && isAuthenticated(requestUser) && this.props.onBlockUser;
        const showWhoLiked = isAuthenticated(requestUser) && post.likeable.likesCount > 0;
        const showEdit = (isAuthor || isAdmin) && !post.deletedAt;
        const showDelete = (isAuthor || isAdmin) && !post.deletedAt;
        const showSetDisplayingAtFeed = isAdmin;
        const isMagazineUser = [MAGAZINE_USER, MK_TESTING_USERNAME, MODRYKONIK_USERNAME].includes(
            post.author.username,
        );
        const showSetShowInMagazine = isAdmin && !isMagazineUser;
        const role = isAuthor ? null : isAdmin ? "admin" : null;

        return showReport || showEdit || showDelete ? (
            <Menu
                className={cx(styles.AlbumMenu, className)}
                menuId={BLOG_ALBUM_MENU_MORE(post.id)}
                trigger={
                    <Btn
                        type={BtnType.BlueOutline}
                        icon={fasCogLg}
                        onClick={this.handleOnToggleMenuMore}
                    />
                }
                triggerShadow={false}
            >
                {showReport && (
                    <MenuItem
                        label={BLOG_REPORT_POST}
                        icon={faExclamationTriangle}
                        onClick={this.handleOnReport}
                    />
                )}

                {showSendHeart && (
                    <MenuItem
                        label={interpolate(SEND_HEART_TO_username, {
                            username: post.author.username,
                        })}
                        icon={faHeart}
                        link={url(
                            heartsUrl,
                            {},
                            {
                                to: post.author.username,
                                next: location.pathname,
                            },
                        )}
                        asAnchor
                    />
                )}

                {/* "Nezobrazuj mi príspevky" hidden — replaced by "Zablokovať" (UserBlock).
                    Redis ignore kept on backend for backward compat, cleanup in separate PR. */}

                {showBlock && (
                    <MenuItem label={BLOCK_USER} icon={faBan} onClick={this.handleBlockUser} />
                )}

                {showEdit && (
                    <MenuItem
                        className={role && styles[`AlbumMenu__item--${role}`]}
                        label={CHANGE_DATE_TITLE_AND_DESCRIPTION}
                        icon={faPencil}
                        link={url(blogsAlbumEditUrl, {
                            username: post.author.username,
                            postId: post.id,
                        })}
                    />
                )}

                {showEdit && post.images.length > 0 && (
                    <MenuItem
                        className={role && styles[`AlbumMenu__item--${role}`]}
                        label={REORDER_PHOTOS}
                        icon={faTh}
                        link={url(blogsAlbumReorderUrl, {
                            username: post.author.username,
                            postId: post.id,
                        })}
                    />
                )}

                {showDelete && (
                    <MenuItem
                        className={role && styles[`AlbumMenu__item--${role}`]}
                        label={DELETE_ALBUM}
                        icon={faTrash}
                        onClick={this.handleOnDelete}
                    />
                )}

                {showWhoLiked && (
                    <MenuItem
                        label={SHOW_USERS_WHO_LIKED_THIS_POST}
                        icon={faThumbsUp}
                        link={url(blogsEntityLikersUrl, {
                            type: "short-message",
                            entityId: post.id,
                        })}
                    />
                )}

                {showSetDisplayingAtFeed && (
                    <MenuItem
                        className={styles[`AlbumMenu__item--admin`]}
                        label={post.isDisplayingAtFeed ? REMOVE_FROM_FEED : RETURN_TO_FEED}
                        icon={post.isDisplayingAtFeed ? faEraser : faUndo}
                        onClick={
                            post.isDisplayingAtFeed
                                ? this.handleRemoveFromFeed
                                : this.handleReturnToFeed
                        }
                    />
                )}

                {showSetShowInMagazine && (
                    <MenuItem
                        className={styles[`AlbumMenu__item--admin`]}
                        label={post.showInMagazine ? REMOVE_FROM_MAGAZINE : ADD_TO_MAGAZINE}
                        onClick={
                            post.showInMagazine
                                ? this.handleRemoveFromMagazine
                                : this.handleAddToMagazine
                        }
                    />
                )}
            </Menu>
        ) : null;
    }

    private handleBlockUser = () => {
        const { post, onBlockUser } = this.props;
        if (onBlockUser) {
            onBlockUser(post.author.id, post.author.username);
        }
    };

    private handleOnReport = () => {
        const { post, onReport, location } = this.props;
        onReport(ComplaintTargetType.PhotoblogPost, post.id, location);
    };

    private handleOnDelete = () => {
        const { post, onDelete } = this.props;
        onDelete(post.id);
    };

    private handleRemoveFromFeed = () => {
        const { post, onSetDisplayingAtFeed } = this.props;
        onSetDisplayingAtFeed(post.id, false);
    };

    private handleReturnToFeed = () => {
        const { post, onSetDisplayingAtFeed } = this.props;
        onSetDisplayingAtFeed(post.id, true);
    };

    private handleAddToMagazine = () => {
        const { post, onSetShowInMagazine } = this.props;
        onSetShowInMagazine(post.id, true);
    };

    private handleRemoveFromMagazine = () => {
        const { post, onSetShowInMagazine } = this.props;
        onSetShowInMagazine(post.id, false);
    };

    private handleOnToggleMenuMore = () => {
        const { post, onToggleMenuMore } = this.props;
        onToggleMenuMore(post.id);
    };
}

export default AlbumMenu;
